/**
 * What has the cursor when a panel opens.
 *
 * Radix focuses the first thing it can tab to, which on every sheet and
 * dialog here is the ✕ in the corner — so a form opened to be filled in
 * started with the cursor on Close, and the first Enter closed it again
 * instead of saving. On a one-field panel like "Add a city" that is the whole
 * interaction going wrong twice over.
 *
 * So: the first field somebody can type in, if there is one. A reader with no
 * fields keeps the panel itself focused, which is what Radix would have done
 * and what a screen reader needs to announce the title.
 */
const TYPEABLE =
  'input:not([type=hidden]):not([type=radio]):not([type=checkbox]),textarea,select,[contenteditable=true]'

export function focusFirstField(e: Event) {
  /* The panel itself: Radix dispatches this on the content node. Read from
     the event rather than from a ref, because the ref's state has not
     reached this closure on the render that mounts the panel. */
  const content = e.currentTarget
  if (!(content instanceof HTMLElement)) return
  const first = [...content.querySelectorAll<HTMLElement>(TYPEABLE)].find(
    (el) =>
      !(el as HTMLInputElement).disabled &&
      !(el as HTMLInputElement).readOnly &&
      /* `hidden`, not `offsetParent`: a field inside a collapsed section is
         not somewhere to put the cursor, and jsdom has no layout to ask. */
      !el.hidden &&
      el.getAttribute('aria-hidden') !== 'true',
  )
  if (!first) return
  e.preventDefault()
  first.focus()
  /* An existing value is selected rather than appended to: a sheet opened on
     a record is opened to change what is there. */
  if (first instanceof HTMLInputElement && first.type === 'text') first.select()
}
